import { MODAL_AUTH } from '@components/App/Modal/constants'
import { LimitReachedActions } from '@store/reducers/limitReached'
import { checkCanSaveProject } from '@store/reducers/manager/sagas/saveProjectSaga'
import { ErrorNotification } from '@storeOld/reducers/notification'
import { openModal } from '@storeOld/reducers/ui'
import { useEffect, useRef, useState } from 'react'
import { useDispatch } from 'react-redux'
import { useLocation, useSearchParams } from 'react-router-dom'

import API from '@/api'
import { GtmTriggerName, sendGtmEvent } from '@/constants/gtm'
import { useAuth } from '@/hooks'

// A guest's report is claimed the way a guest's sitemap is: Save asks them to
// log in, the login comes back to the report with `?save`, and the PHP save
// route makes it theirs. Over the plan's project limit it stays the guest's
// report — still on screen, Save still there — and the upsell is shown instead.
export const useSaveGuestReport = (
  uuid: string | undefined,
  ownerless: boolean,
  onSaved: () => void
) => {
  const dispatch = useDispatch()
  const { pathname } = useLocation()
  const [searchParams, setSearchParams] = useSearchParams()
  const { authenticated, setupCompleted } = useAuth()
  const [saving, setSaving] = useState(false)
  const claiming = useRef(false)

  const claim = async () => {
    if (!uuid || claiming.current) {
      return
    }

    claiming.current = true
    setSaving(true)

    try {
      const { data } = await checkCanSaveProject()

      if (!data.canSave) {
        dispatch(LimitReachedActions.alert())

        return
      }

      await API.Projects.save({ id: uuid })

      sendGtmEvent(GtmTriggerName.REPORT_GUEST_SAVED)
      onSaved()
    } catch {
      dispatch(ErrorNotification('Could not save the report. Try again.'))
    } finally {
      claiming.current = false
      setSaving(false)
    }
  }

  useEffect(() => {
    if (!searchParams.has('save') || !ownerless) {
      return
    }

    if (authenticated && setupCompleted) {
      searchParams.delete('save')
      setSearchParams(searchParams, { replace: true })
      claim()
    }
  }, [authenticated, ownerless, searchParams, setupCompleted])

  const save = () => {
    sendGtmEvent(GtmTriggerName.REPORT_GUEST_SAVE_CLICK)

    if (authenticated) {
      claim()

      return
    }

    dispatch(
      // @ts-expect-error - openModal is untyped
      openModal(MODAL_AUTH, { redirectAfterAuth: `${pathname}?save` })
    )
  }

  return { save, saving }
}
